import type { HTMLAttributes, ReactNode } from 'react'
import { AlertCircle, ArrowRight, ChevronLeft, ChevronRight, Info, TriangleAlert } from 'lucide-react'
import type { RuleHit } from '@/admin/hooks/use-validation-rules'
import { Button } from '@/shared/components/ui/button'
import { cn } from '@/shared/lib/utils'
import { useWizardValidation } from './wizard-validation'

interface RegistrationPanelProps extends HTMLAttributes<HTMLDivElement> {
  title: string
  icon: ReactNode
  children: ReactNode
  bodyClassName?: string
  headerAction?: ReactNode
}

export function RegistrationPanel({
  title,
  icon,
  children,
  className,
  bodyClassName,
  headerAction,
  ...props
}: RegistrationPanelProps) {
  return (
    <div
      className={cn('overflow-hidden rounded-[8px] border-[0.5px] border-[var(--rule)]', className)}
      {...props}
    >
      <div className="flex h-[48px] items-center justify-between gap-3 bg-[var(--bg-subtle)] px-5">
        <div className="flex min-w-0 items-center gap-2">
          <span className="shrink-0 text-[var(--ink-3)]">{icon}</span>
          <p className="m-0 truncate text-xs font-normal uppercase tracking-[0.14em] text-[var(--ink-3)]">
            {title}
          </p>
        </div>
        {headerAction}
      </div>
      <div className={cn('flex flex-col gap-4 bg-[var(--bg)] p-5', bodyClassName)}>
        {children}
      </div>
    </div>
  )
}

export function RegistrationStepGrid({
  children,
  className,
  ...props
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('grid grid-cols-1 items-stretch gap-6 lg:grid-cols-2', className)}
      {...props}
    >
      {children}
    </div>
  )
}

interface RegistrationDisclosureSectionProps extends HTMLAttributes<HTMLDivElement> {
  label: string
  open: boolean
  onToggle: () => void
  children?: ReactNode
  contentClassName?: string
}

export function RegistrationDisclosureSection({
  label,
  open,
  onToggle,
  children,
  className,
  contentClassName,
  ...props
}: RegistrationDisclosureSectionProps) {
  return (
    <div className={cn('flex flex-col', className)} {...props}>
      <Button
        variant="ghost"
        type="button"
        onClick={onToggle}
        className="h-auto w-fit cursor-pointer justify-start gap-1.5 px-0 py-0 text-[12px] font-light text-[var(--ink-3)] transition-colors duration-150 hover:bg-transparent hover:text-[var(--ink)]"
      >
        <ChevronRight
          size={12}
          className={cn(
            'shrink-0 transition-transform duration-150',
            open && 'rotate-90 text-[var(--ink)]',
          )}
        />
        <span>{label}</span>
      </Button>
      {open ? (
        <div className={cn('mt-3 flex flex-col gap-3', contentClassName)}>
          {children}
        </div>
      ) : null}
    </div>
  )
}

const ALERT_META: Record<
  RuleHit['severity'],
  {
    title: string
    icon: ReactNode
    wrapperClassName: string
    titleClassName: string
    bodyClassName: string
  }
> = {
  BLOCK: {
    title: 'Error',
    icon: <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-[var(--status-error)]" />,
    wrapperClassName: 'border-[rgba(220,38,38,0.2)] bg-[rgba(220,38,38,0.06)]',
    titleClassName: 'text-[var(--status-error)]',
    bodyClassName: 'text-[rgb(127,29,29)]',
  },
  WARN: {
    title: 'Warning',
    icon: <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0 text-[rgb(180,83,9)]" />,
    wrapperClassName: 'border-[rgba(245,158,11,0.28)] bg-[rgba(245,158,11,0.08)]',
    titleClassName: 'text-[rgb(146,64,14)]',
    bodyClassName: 'text-[rgb(120,53,15)]',
  },
  INFO: {
    title: 'Notice',
    icon: <Info className="mt-0.5 h-4 w-4 shrink-0 text-[rgb(29,78,216)]" />,
    wrapperClassName: 'border-[rgba(59,130,246,0.22)] bg-[rgba(59,130,246,0.08)]',
    titleClassName: 'text-[rgb(30,64,175)]',
    bodyClassName: 'text-[rgb(30,58,138)]',
  },
}

export function RegistrationRuleAlerts({ hits }: { hits: RuleHit[] }) {
  if (hits.length === 0) return null

  const groupedHits = {
    BLOCK: hits.filter((hit) => hit.severity === 'BLOCK'),
    WARN: hits.filter((hit) => hit.severity === 'WARN'),
    INFO: hits.filter((hit) => hit.severity === 'INFO'),
  } satisfies Record<RuleHit['severity'], RuleHit[]>

  return (
    <div className="mb-5 space-y-3">
      {(['BLOCK', 'WARN', 'INFO'] as const).map((severity) => {
        const severityHits = groupedHits[severity]
        if (severityHits.length === 0) return null

        const meta = ALERT_META[severity]

        return (
          <div
            key={severity}
            className={cn(
              'rounded-lg border px-4 py-3',
              meta.wrapperClassName,
            )}
          >
            <div className="flex items-start gap-3">
              {meta.icon}
              <div className="min-w-0 flex-1">
                <p
                  className={cn(
                    'm-0 text-[12px] font-light uppercase tracking-[0.08em]',
                    meta.titleClassName,
                  )}
                >
                  {meta.title}
                </p>
                <div className="mt-1 space-y-1">
                  {severityHits.map((hit) => (
                    <p
                      key={hit.rule_uuid}
                      className={cn('m-0 text-[13px]', meta.bodyClassName)}
                    >
                      {hit.message}
                    </p>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}

interface RegistrationStepActionsProps {
  onContinue: () => void
  /**
   * Legacy prop. The Continue button is now always enabled across the
   * wizard; clicking surfaces inline per-field errors via the wizard
   * validation context. The prop is accepted for backward compatibility
   * with existing call sites but is intentionally ignored.
   */
  continueDisabled?: boolean
  continueLabel?: string
  /**
   * Optional informational hint rendered above the Continue button.
   * Reserved for non-validation messages (e.g. rule warnings) - required
   * field errors should render inline next to the field instead.
   */
  continueHint?: string | null
  onBack?: () => void
  backLabel?: string
  className?: string
  /**
   * Deprecated. The action bar is now always sticky-bottom with a
   * top hairline. Prop kept on the interface so callers compile,
   * but the value is ignored.
   */
  withDivider?: boolean
  align?: 'between' | 'end'
}

export function RegistrationStepActions({
  onContinue,
  continueLabel = 'Continue',
  continueHint,
  onBack,
  backLabel = 'Back',
  className,
  align = 'between',
}: RegistrationStepActionsProps) {
  const { attemptContinue } = useWizardValidation()
  const justifyClass = align === 'end' || !onBack ? 'justify-end' : 'justify-between'

  // Sticky bottom on tall steps (e.g. registration wizard's product
  // picker on tenants with 100+ categories) so the Continue button
  // never drifts below the fold. Adds a soft backdrop + top hairline
  // so it visually separates from the scrolling content above.
  return (
    <div className={cn('sticky bottom-0 z-10 -mx-2 mt-6 flex items-center gap-3 border-t-[0.5px] border-t-[var(--rule)] bg-[var(--bg)]/95 px-2 py-3 backdrop-blur-sm', justifyClass, className)}>
      {onBack ? (
        <Button
          variant="ghost"
          type="button"
          onClick={onBack}
          className="flex cursor-pointer items-center gap-1 border-none bg-transparent px-0 text-[10px] font-light uppercase tracking-[0.14em] text-[var(--ink-3)] transition-all duration-150 ease hover:text-[var(--ink)]"
        >
          <ChevronLeft size={13} />
          {backLabel}
        </Button>
      ) : null}
      <div className="flex flex-col items-end gap-1">
        {continueHint ? (
          <p className="max-w-[320px] text-right text-[11px] text-[var(--status-error)]">
            {continueHint}
          </p>
        ) : null}
        <Button
          type="button"
          onClick={() => attemptContinue(onContinue)}
          className="flex h-9 cursor-pointer items-center gap-1.5 rounded-[4px] border-none bg-[var(--green)] px-4 text-[10px] font-light uppercase tracking-[0.14em] text-white transition-all duration-150 ease hover:opacity-90"
        >
          {continueLabel}
          <ArrowRight size={13} />
        </Button>
      </div>
    </div>
  )
}
